import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SmartMarkdownLink } from '../components/SmartMarkdownLink';

export const PREVIEW_STORAGE_KEY = 'doctivity_announcement_preview';

/**
 * Standalone, no-chrome preview of an announcement draft, opened in a new tab
 * from the create/edit dialog (see App.js bypass). The draft is handed over via
 * localStorage (same origin) rather than the server, since it isn't saved yet.
 * Renders the same way the real note does: media on top, then title, then the
 * Markdown body.
 */
const AnnouncementPreviewRoot = () => {
  const [draft, setDraft] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    document.title = 'Announcement Preview';
    try {
      const raw = window.localStorage.getItem(PREVIEW_STORAGE_KEY);
      setDraft(raw ? JSON.parse(raw) : null);
    } catch (e) {
      setDraft(null);
    }
    setLoaded(true);
  }, []);

  const wrap = {
    maxWidth: 720,
    margin: '0 auto',
    padding: '32px 24px 64px',
    fontFamily: 'Helvetica, Arial, sans-serif',
    color: '#222222',
    lineHeight: 1.6,
  };

  if (loaded && (!draft || (!draft.title && !draft.body_markdown))) {
    return (
      <div style={wrap}>
        <p>Nothing to preview yet. Compose an announcement, then click “Preview Announcement”.</p>
      </div>
    );
  }

  if (!draft) return <div style={wrap} />;

  return (
    <div style={wrap}>
      <style>{`
        .ann-preview code { background:#f2f4f7; padding:2px 5px; border-radius:3px; font-size:0.9em; }
        .ann-preview pre { background:#f6f8fa; padding:12px 14px; border-radius:6px; overflow:auto; }
        .ann-preview pre code { background:none; padding:0; }
        .ann-preview table { border-collapse:collapse; margin:8px 0; }
        .ann-preview th, .ann-preview td { border:1px solid #c9d4e2; padding:6px 12px; }
        .ann-preview th { background:#f4f7fb; }
        .ann-preview a { color:#1565c0; }
        .ann-preview blockquote { margin:8px 0; padding:4px 14px; border-left:4px solid #c9d4e2; color:#555; }
        .ann-preview hr { border:none; border-top:1px solid #dfe4ea; margin:24px 0; }
      `}</style>

      {draft.title && <h2 style={{ marginTop: 0 }}>{draft.title}</h2>}

      <div className='ann-preview'>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: SmartMarkdownLink }}>
          {draft.body_markdown || ''}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export { AnnouncementPreviewRoot };
export default AnnouncementPreviewRoot;
